import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './CaseStudyPage.css';

const GraduateIndustryProject = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('');
    const [readingProgress, setReadingProgress] = useState(0);

    // Table of contents (Results first)
    const tableOfContents = useMemo(() => [
        { id: 'results', title: 'Results & Impact' },
        { id: 'technologies', title: 'Technologies Used' },
        { id: 'overview', title: 'Project Overview' },
        { id: 'challenge', title: 'The Challenge' },
        { id: 'solution', title: 'Solution & Implementation' }
    ], []);

    // Scroll handling
    useEffect(() => {
        const handleScroll = () => {
            const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
            const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const scrolled = height > 0 ? (winScroll / height) * 100 : 0;
            setReadingProgress(scrolled);

            const sections = tableOfContents
                .map(item => document.getElementById(item.id))
                .filter(Boolean);

            const scrollPosition = window.scrollY + 200;
            for (let i = sections.length - 1; i >= 0; i--) {
                if (sections[i] && sections[i].offsetTop <= scrollPosition) {
                    setActiveSection(sections[i].id);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // init
        return () => window.removeEventListener('scroll', handleScroll);
    }, [tableOfContents]);

    const handleBackToCaseStudies = () => {
        navigate('/');
        setTimeout(() => {
            const mycaseSection = document.getElementById('mycase');
            if (mycaseSection) {
                mycaseSection.scrollIntoView({ behavior: 'auto', block: 'start' });
            }
        }, 100);
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="case-study-page">
            {/* Reading Progress */}
            <div className="reading-progress-container">
                <div
                    className="reading-progress-bar"
                    style={{ height: `${readingProgress}%` }}
                ></div>
            </div>

            {/* Simple TOC */}
            <div className="table-of-contents">
                <h4>Contents</h4>
                <ul>
                    {tableOfContents.map((item, idx) => (
                        <li key={item.id || idx}>
                            <button
                                className={`toc-item ${activeSection === item.id ? 'active' : ''}`}
                                onClick={() => scrollToSection(item.id)}
                            >
                                {item.title}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="case-study-container">
                {/* Header */}
                <div className="case-study-header">
                    {/* Breadcrumb */}
                    <nav className="breadcrumb">
                        <button className="breadcrumb-item" onClick={() => navigate('/')}>
                            <HomeOutlined /> Home
                        </button>
                        <span className="breadcrumb-separator">/</span>
                        <button className="breadcrumb-item" onClick={handleBackToCaseStudies}>
                            Case Studies
                        </button>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-current">Graduate Industry Project</span>
                    </nav>

                    <button className="back-button" onClick={handleBackToCaseStudies}>
                        <ArrowLeftOutlined /> Back to Case Studies
                    </button>

                    <div className="case-study-hero">
                        <div className="case-study-image">
                            <img src="/images/case/team.jpg" alt="Graduate Industry Project" />
                        </div>

                        <div className="case-study-info">
                            <h1 className="case-study-title">Family Support Web Platform</h1>
                            <p className="case-study-subtitle">Accessible Resources • Expert Advice • Community Support</p>

                            <div className="case-study-meta">
                                <div className="meta-item">
                                    <span className="meta-label">Duration:</span>
                                    {/* Resume: 2025/03 – 2025/06 ⇒ ≈ 4 months */}
                                    <span className="meta-value">~4 months</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Team:</span>
                                    <span className="meta-value">Multidisciplinary cohort project (uni-sponsored)</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Roles:</span>
                                    {/* Based on your note + resume scope */}
                                    <span className="meta-value">Product & Project Coordination • UI/UX • Frontend • Testing</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Status:</span>
                                    <span className="meta-value status-active">Delivered (Program Project)</span>
                                </div>
                                {/* If you later confirm award details, replace the line below and remove the comment */}
                                {/* <div className="meta-item">
                  <span className="meta-label">Recognition:</span>
                  <span className="meta-value status-production">Awarded in Program Showcase (details)</span>
                </div> */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="case-study-content">
                    {/* Results & Impact */}
                    <section className="content-section" id="results">
                        <h2>Results &amp; Impact</h2>
                        <p>
                            Delivered a web platform tailored for parents of autistic children, emphasizing <strong>accessibility</strong>,
                            <strong> clarity of navigation</strong>, and <strong>content discoverability</strong>. The project instituted a disciplined
                            feedback loop (weekly stakeholder reviews) and shipped incrementally with <strong>manual + automated tests</strong> guarding
                            core flows (e.g., sign-up, resource search, form submissions, and content loading).
                        </p>

                        <div className="results-grid">
                            <div className="result-card">
                                <div className="result-number">Inclusive</div>
                                <div className="result-label">Accessibility-First UI</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Iterative</div>
                                <div className="result-label">Weekly Stakeholder Reviews</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Tested</div>
                                <div className="result-label">Manual + Automated QA</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Calm</div>
                                <div className="result-label">Stable Release Rhythm</div>
                            </div>
                        </div>

                        {/* Mobile Results */}
                        <div className="mobile-results-row">
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Inclusive</div>
                                </div>
                                <div className="mobile-result-label">Accessibility</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Iterative</div>
                                </div>
                                <div className="mobile-result-label">Stakeholders</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Tested</div>
                                </div>
                                <div className="mobile-result-label">QA</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Calm</div>
                                </div>
                                <div className="mobile-result-label">Releases</div>
                            </div>
                        </div>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* Technologies */}
                    <section className="content-section" id="technologies">
                        <h2>Technologies Used</h2>
                        <div className="tech-stack">
                            {/* Resume-backed items; no speculative frameworks */}
                            <span className="tech-tag">UI/UX with Figma</span>
                            <span className="tech-tag">Web Frontend (JavaScript)</span>
                            <span className="tech-tag">Accessibility Principles</span>
                            <span className="tech-tag">Manual &amp; Automated Testing</span>
                            <span className="tech-tag">Agile Iterations / Sprint Reviews</span>
                            {/* If you later confirm specific FE stack (e.g., React), add a tag here */}
                            {/* <span className="tech-tag">React</span> */}
                        </div>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* Project Overview */}
                    <section className="content-section" id="overview">
                        <h2>Project Overview</h2>
                        <p>
                            As part of the graduate industry program, our multidisciplinary team built a <strong>web-based platform</strong>
                            to help parents of autistic children access <strong>personalized resources</strong>, connect with <strong>expert advice</strong>,
                            and participate in a <strong>supportive community</strong>. I operated in multiple roles—coordinating product direction and
                            delivery, designing UI/UX, implementing frontend pieces, and establishing a practical testing approach.
                            {/* If you later verify AI usage, clarify scope here (e.g., rules-based personalization vs. ML). */}
                        </p>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* The Challenge */}
                    <section className="content-section" id="challenge">
                        <h2>The Challenge</h2>
                        <p>
                            We needed to serve a diverse audience with varying levels of digital literacy while ensuring <strong>clarity</strong>,
                            <strong> accessibility</strong>, and <strong>trustworthiness</strong> of information. Time constraints of an academic term required tight
                            scope control, dependable sprint cadence, and the ability to switch quickly between <em>PM planning</em>, <em>design</em>, <em>coding</em>,
                            and <em>testing</em>—often within the same week.
                        </p>

                        <div className="challenge-grid">
                            <div className="challenge-item">
                                <h4>Accessibility & Usability</h4>
                                <p>Design for inclusive access and clear navigation across user groups.</p>
                            </div>
                            <div className="challenge-item">
                                <h4>Content Findability</h4>
                                <p>Help parents discover relevant, trustworthy resources quickly.</p>
                            </div>
                            <div className="challenge-item">
                                <h4>Time-Boxed Delivery</h4>
                                <p>Maintain quality while shipping on a short academic timeline.</p>
                            </div>
                            <div className="challenge-item">
                                <h4>Multi-Role Load</h4>
                                <p>Balance PM, design, frontend, and test ownership under pressure.</p>
                            </div>
                        </div>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* Solution & Implementation */}
                    <section className="content-section" id="solution">
                        <h2>Solution &amp; Implementation</h2>
                        <p>
                            We structured the delivery around weekly stakeholder check-ins, design-first iteration, and test-guarded releases.
                            My focus was orchestrating outcomes across roles to keep velocity high without sacrificing quality.
                        </p>

                        <div className="solution-features">
                            <div className="feature-item">
                                <h4>Product & Project Coordination</h4>
                                <p>
                                    Defined MVP scope and prioritized a high-signal feature set (onboarding, resource discovery, guided content paths).
                                    Ran sprint ceremonies and kept an inspection/adaptation loop with stakeholders.
                                </p>
                            </div>
                            <div className="feature-item">
                                <h4>UI/UX Design (Figma)</h4>
                                <p>
                                    Created accessible component patterns (forms, navigation, cards), simplified information hierarchy,
                                    and validated flows with low/high-fidelity prototypes before development.
                                </p>
                            </div>
                            <div className="feature-item">
                                <h4>Frontend Implementation</h4>
                                <p>
                                    Built core UI interactions and forms, focusing on input validation, error states, and performance on common devices.
                                </p>
                            </div>
                            <div className="feature-item">
                                <h4>Testing & Quality Assurance</h4>
                                <p>
                                    Authored <strong>manual test cases</strong> for primary flows and set up <strong>automated checks</strong> to catch regressions
                                    in UI interactions, form submissions, and basic data performance.
                                </p>
                            </div>
                            <div className="feature-item">
                                <h4>Stakeholder Feedback & Iteration</h4>
                                <p>
                                    Demoed progress <strong>weekly</strong>, collected qualitative feedback, and iterated quickly—
                                    especially on navigation clarity and content labelling to reduce cognitive load.
                                </p>
                            </div>
                        </div>

                        {/* Section Divider */}
                        <div className="section-divider"></div>

                        <h2>Results &amp; Impact</h2>
                        <p>
                            The final delivery balanced inclusivity with clarity, and established a repeatable rhythm for
                            building, validating, and refining features under tight timelines. The emphasis on accessibility,
                            structured testing, and stakeholder-driven iteration created a platform that felt both <em>approachable</em>
                            and <em>reliable</em> to its target audience.
                            {/* If award is later confirmed, add a line here to note the recognition with specifics. */}
                        </p>

                        {/* <div className="results-grid">
                            <div className="result-card">
                                <div className="result-number">Accessible</div>
                                <div className="result-label">Inclusive UI Patterns</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Reliable</div>
                                <div className="result-label">Guarded by Tests</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Focused</div>
                                <div className="result-label">MVP Scope Discipline</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Responsive</div>
                                <div className="result-label">Stakeholder-Led Iterations</div>
                            </div>
                        </div>

                        <div className="mobile-results-row">
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Accessible</div>
                                </div>
                                <div className="mobile-result-label">UI</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Reliable</div>
                                </div>
                                <div className="mobile-result-label">Tests</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Focused</div>
                                </div>
                                <div className="mobile-result-label">MVP</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Responsive</div>
                                </div>
                                <div className="mobile-result-label">Feedback</div>
                            </div>
                        </div> */}

                        {/* Project Recognition Image */}
                        <div className="project-recognition">
                            <div className="recognition-image">
                                <img src="/images/rewards/project.png" alt="Project Recognition Award" />
                            </div>
                        </div>
                    </section>

                    {/* Back to Case Studies at the end */}

                    <div style={{ textAlign: 'left', margin: '0px 0', paddingTop: '20px', borderTop: '1px solid #e8e8e8' }}>
                        <button className="back-button" onClick={handleBackToCaseStudies}>
                            <ArrowLeftOutlined /> Back to Case Studies
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GraduateIndustryProject;
